import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  Col, Row, Spin, Tooltip,
} from 'antd';
import axios from 'axios';

import {
  getCustomTables,
  getStatuses,
} from '../forms/state/forms.actions';
import { getBillingRates } from '../billingRates/state/billingRates.actions';
import { getCustomers } from '../contacts/customers/state/customers.actions';
import { getUserTeams } from '../users/state/users.actions';
import { getProjectEquipment } from '../projects/state/projects.actions';
import { getEquipment } from '../equipment/state/equipment.actions';
import { getCustomFields } from '../timecards/state/timecards.actions';
import { getLabels } from '../labels/state/labels.actions';
import {
  getAllBoardsDetails,
  getBoardCardTemplates,
  getBoards,
  getBoardStatuses,
  getBoardColors,
  getShortLists,
} from '../boards/state/boards.actions';
import useToggle from '../common/hooks/useToggle';
import BoardsSearchDrawer from './BoardsSearchDrawer';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import BoardsResults from './BoardsResults';
import AdvancedSearchAnalyticsDrawer from './AdvancedSearchAnalyticsDrawer';
import SearchAnalyticsFilterBanner from './SearchAnalyticsFilterBanner';
import { getBuckets } from '../buckets/state/buckets.actions';

import { request } from '../helpers/requests';
import { parseFormResponseToReadableFormat } from '../forms/ResponderHelpers';
import ReportExcelExport from '../reports/reportExcelExport';

import { getType } from '../forms/FormWorkflows/workflowHelpers.js';

import { textBasedFieldTypes } from '../forms/formHelpers';

import { getProjectIdMap } from '../state/selectors';

const MAX_EXPORT_RESULTS = 10000;
const BATCH_SIZE = 1000;
const TOOLTIP_TITLE = `Exporting is disabled for searches with more than ${MAX_EXPORT_RESULTS} results.`;

const exporter = new ReportExcelExport();
export default function BoardsSearch() {
  const dispatch = useDispatch();

  const results = useSelector((state) => state.search.boardResults);
  const formFieldValues = useSelector((state) => state.search.formFieldValues);
  const selectedQuery = useSelector((state) => state.search.selectedQuery);
  const currentQuery = useSelector((state) => state.search.currentQuery);
  const cardTypeId = useSelector((state) => state.search.cardTypeId);
  const boardCardTemplates = useSelector((state) => state.boards.cardTemplates);
  const settings = useSelector((state) => state.settings.settings);

  const projectIdMap = useSelector(getProjectIdMap);
  const numResults = results?.length ?? 0;

  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [analyticsFilter, setAnalyticsFilter] = useState(null);

  const { isToggled: drawerVisible, toggle: toggleVisible } = useToggle();
  const { isToggled: exportLoading, toggle: toggleExportLoading } = useToggle();
  const { isToggled: analyticsDrawerVisible, toggle: toggleAnalyticsDrawerVisible } = useToggle();

  const onClose = useCallback(async (promise) => {
    toggleVisible();
    if (promise) {
      setSearching(true);
      await promise;
      setSearching(false);
    }
  }, [toggleVisible]);

  const hasAnalyticsFields = useMemo(() => {
    const queryToUse = currentQuery ?? selectedQuery;
    return queryToUse?.type === 'boards'
      && (queryToUse?.query?.formulas?.filter((f) => f.analytics).length ?? 0) > 0;
  }, [selectedQuery, currentQuery]);

  const filteredResults = useMemo(() => {
    if (!analyticsFilter) return results;
    const { fieldId, value } = analyticsFilter;
    return results.filter((card) => formFieldValues[card.id]?.[fieldId] === value);
  }, [results, formFieldValues, analyticsFilter]);

  useEffect(() => {
    setAnalyticsFilter(null);
  }, [results]);

  const onExport = async () => {
    const ourTemplate = boardCardTemplates[cardTypeId];
    if (!ourTemplate || numResults > MAX_EXPORT_RESULTS) return;
    const {
      useCardNumberAsCardTitle,
      title: templateTitle,
      fields: templateSections = [],
    } = ourTemplate;
    toggleExportLoading();

    const allFullCards = [];

    for (let i = 0; i < numResults; i += BATCH_SIZE) {
      const batch = results.slice(i, i + BATCH_SIZE);
      const {
        data: fullCards = [],
      } = await request({
        call: axios.post('/boards/exports/data', {
          ids: batch.map((row) => row.id),
        }),
        errMsg: 'Failed to export cards',
        hideSuccessToast: true,
      });

      allFullCards.push(...fullCards);

      if (i + BATCH_SIZE < numResults) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    if (allFullCards.length === 0) {
      toggleExportLoading();
      return;
    }
    const columns = [];
    if (useCardNumberAsCardTitle) {
      columns.push({
        title: 'Card Number',
        field: 'number',
      });
    } else {
      columns.push({
        title: 'Card Title',
        field: 'title',
      });
    }
    columns.push({
      title: 'Status',
      field: 'status',
    });
    const fieldTitleMap = {};
    templateSections.forEach((section) => {
      section.fields?.forEach((field) => {
        if (!field.configProps?.title) return;
        const { selectedType, configProps } = field ?? {};
        if (selectedType === 'table' && configProps?.dataType === 'TimeEntry') {
          fieldTitleMap[field.id] = configProps.title;
          return;
        }
        const type = selectedType === 'dynamicAttribute' ? getType(field) : selectedType;
        if (!textBasedFieldTypes.has(type)) return;
        columns.push({
          title: configProps.title,
          field: field.id,
        });
      });
    });

    const resultDateMap = {};
    results.forEach((r) => {
      if (r.mostRecentDates) resultDateMap[r.id] = r.mostRecentDates;
    });

    const mostRecentDateFieldIds = new Set();
    const data = allFullCards.map((card) => {
      const {
        id,
        title,
        status,
        number,
        data: responseSections,
      } = card;
      const row = {
        id,
        title: useCardNumberAsCardTitle ? number : title,
        number,
        status,
        ...parseFormResponseToReadableFormat({
          templateSections,
          responseSections,
          projectIdMap,
          settings,
        }),
      };
      const dates = resultDateMap[id];
      if (dates) {
        Object.entries(dates).forEach(([fieldId, value]) => {
          row[fieldId] = value || '';
          mostRecentDateFieldIds.add(fieldId);
        });
      }
      return row;
    });

    mostRecentDateFieldIds.forEach((fieldId) => {
      const fieldTitle = fieldTitleMap[fieldId] || fieldId;
      columns.push({
        title: `${fieldTitle}: Most Recent Day Worked`,
        field: fieldId,
      });
    });
    exporter.createBoardExport(templateTitle, { columns, data });
    toggleExportLoading();
  };

  useEffect(() => {
    const get = async () => {
      setLoading(true);
      await Promise.all([
        dispatch(getBillingRates()),
        dispatch(getStatuses()),
        dispatch(getCustomers()),
        dispatch(getCustomTables()),
        dispatch(getUserTeams()),
        dispatch(getEquipment()),
        dispatch(getProjectEquipment()),
        dispatch(getCustomFields()),
        dispatch(getBoards()),
        dispatch(getBoardCardTemplates()),
        dispatch(getLabels()),
        dispatch(getAllBoardsDetails()),
        dispatch(getBoardStatuses()),
        dispatch(getBoardColors()),
        dispatch(getBuckets()),
        dispatch(getShortLists()),
      ]);
      setLoading(false);
    };
    get();
  }, []);

  const SearchButton = useMemo(() => (
    <>
      <OnTraccrButton
        title="Open Search"
        icon={<SearchOutlined />}
        style={{ marginLeft: 10 }}
        onClick={toggleVisible}
        disabled={loading || searching}
      />
      {(loading || searching) ?? <Spin /> }
    </>
  ), [toggleVisible, loading, searching]);

  const Parent = numResults > MAX_EXPORT_RESULTS ? Tooltip : React.Fragment;

  return (
    <div>
      <Col style={{ width: '100%' }}>
        <SearchAnalyticsFilterBanner
          activeFilter={analyticsFilter}
          filteredCount={filteredResults.length}
          totalCount={results.length}
          onClear={() => setAnalyticsFilter(null)}
        />
        <Row style={{ width: '100%' }} gutter={15}>
          {SearchButton}
          {numResults > 0 && !loading && !searching && (
            <Col>
              <Parent title={TOOLTIP_TITLE} trigger="click">
                <OnTraccrButton
                  title="Export"
                  loading={exportLoading}
                  onClick={onExport}
                  disabled={exportLoading || numResults === 0 || numResults > MAX_EXPORT_RESULTS}
                />
              </Parent>
            </Col>
          )}
          {numResults > 0 && hasAnalyticsFields && (
            <Col>
              <OnTraccrButton
                title="Analytics"
                onClick={toggleAnalyticsDrawerVisible}
              />
            </Col>
          )}
        </Row>
        <Row
          style={{
            width: '100%',
            height: 'calc(-160px + 100vh)',
            paddingTop: '10px',
            overflow: 'scroll',
          }}
        >
          <BoardsResults
            loading={(loading || searching)}
            results={filteredResults}
          />
        </Row>
        <BoardsSearchDrawer
          visible={drawerVisible}
          onClose={onClose}
        />
        {numResults > 0 && hasAnalyticsFields && (
          <AdvancedSearchAnalyticsDrawer
            visible={analyticsDrawerVisible}
            onClose={toggleAnalyticsDrawerVisible}
            activeFilter={analyticsFilter}
            onFilterChange={setAnalyticsFilter}
          />
        )}
      </Col>
    </div>
  );
}
